'use client';

import React, { useEffect, useState } from 'react';
import { Card, Text, Title } from '@tremor/react';
import { Input } from '../../../ui/form/Input';
import { Textarea } from '../../../ui/form/Textarea';
import configManagement from '../../../network/configManagement';
import { useToast } from '../../../utils/useToast';

type LeaveRequestHandling = 'undecided' | 'guidance_only' | 'auto_submit';

type EmployeeSelfServiceConfig = {
  hrisSourceSystem: string;
  ticketingSystem: string;
  leaveRequestHandling: LeaveRequestHandling;
  policyVariations: string;
  complianceFrameworks: string;
};

const defaultSettings: EmployeeSelfServiceConfig = {
  hrisSourceSystem: '',
  ticketingSystem: '',
  leaveRequestHandling: 'undecided',
  policyVariations: '',
  complianceFrameworks: '',
};

export function EmployeeSelfServiceSettings() {
  const [settings, setSettings] =
    useState<EmployeeSelfServiceConfig>(defaultSettings);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    let isMounted = true;
    const fetchConfig = async () => {
      setIsLoading(true);
      try {
        const { data } = await configManagement.getConfig();
        const employeeSelfService = data?.data?.employeeSelfService || {};
        if (!isMounted) return;
        setSettings({
          hrisSourceSystem: employeeSelfService.hrisSourceSystem || '',
          ticketingSystem: employeeSelfService.ticketingSystem || '',
          leaveRequestHandling:
            employeeSelfService.leaveRequestHandling || 'undecided',
          policyVariations: employeeSelfService.policyVariations || '',
          complianceFrameworks: employeeSelfService.complianceFrameworks || '',
        });
      } catch (error: any) {
        toast({
          title: 'Load failed',
          description: error?.message || 'Unable to load settings.',
          variant: 'error',
        });
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchConfig();
    return () => {
      isMounted = false;
    };
  }, [toast]);

  const handleSave = async () => {
    setIsSaving(true);
    const loadingToast = toast({
      title: 'Saving...',
      description: 'Updating employee self-service settings',
      variant: 'loading',
      duration: Infinity,
    });

    try {
      await configManagement.updateConfig({
        employeeSelfService: settings,
      });
      loadingToast.dismiss();
      toast({
        title: 'Saved',
        description: 'Employee self-service settings updated.',
        variant: 'success',
      });
    } catch (error: any) {
      loadingToast.dismiss();
      toast({
        title: 'Save failed',
        description: error?.message || 'Unable to update settings.',
        variant: 'error',
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Card className="mt-6">
        <Title>Employee Self-Service</Title>
        <Text>Loading employee self-service settings...</Text>
      </Card>
    );
  }

  return (
    <Card className="mt-6">
      <Title>Employee Self-Service</Title>
      <Text>
        Configure the sources and policies used by the HR helpdesk assistant.
      </Text>

      <div className="mt-6 space-y-6">
        <div>
          <label
            htmlFor="hris_source_system"
            className="mb-1 block text-sm font-medium"
          >
            HRIS/Payroll system of record
          </label>
          <Input
            id="hris_source_system"
            name="hris_source_system"
            value={settings.hrisSourceSystem}
            onChange={(event) =>
              setSettings((prev) => ({
                ...prev,
                hrisSourceSystem: event.target.value,
              }))
            }
            placeholder="e.g., Workday, BambooHR"
            disabled={isSaving}
          />
        </div>

        <div>
          <label
            htmlFor="ticketing_system"
            className="mb-1 block text-sm font-medium"
          >
            Ticketing system
          </label>
          <Input
            id="ticketing_system"
            name="ticketing_system"
            value={settings.ticketingSystem}
            onChange={(event) =>
              setSettings((prev) => ({
                ...prev,
                ticketingSystem: event.target.value,
              }))
            }
            placeholder="e.g., Zendesk, Jira Service Management"
            disabled={isSaving}
          />
        </div>

        <div>
          <label
            htmlFor="leave_request_handling"
            className="mb-1 block text-sm font-medium"
          >
            Leave request handling
          </label>
          <select
            id="leave_request_handling"
            name="leave_request_handling"
            className="mt-1 block w-full rounded border border-gray-300 px-3 py-2 text-sm"
            value={settings.leaveRequestHandling}
            onChange={(event) =>
              setSettings((prev) => ({
                ...prev,
                leaveRequestHandling: event.target
                  .value as LeaveRequestHandling,
              }))
            }
            disabled={isSaving}
          >
            <option value="undecided">Undecided</option>
            <option value="guidance_only">Guidance only</option>
            <option value="auto_submit">Auto submit</option>
          </select>
        </div>

        <div>
          <label
            htmlFor="policy_variations"
            className="mb-1 block text-sm font-medium"
          >
            Policy variations
          </label>
          <Textarea
            id="policy_variations"
            name="policy_variations"
            value={settings.policyVariations}
            onChange={(event) =>
              setSettings((prev) => ({
                ...prev,
                policyVariations: event.target.value,
              }))
            }
            placeholder="Describe region, union, or role-specific variations."
            rows={3}
            disabled={isSaving}
          />
        </div>

        <div>
          <label
            htmlFor="compliance_frameworks"
            className="mb-1 block text-sm font-medium"
          >
            Compliance frameworks
          </label>
          <Textarea
            id="compliance_frameworks"
            name="compliance_frameworks"
            value={settings.complianceFrameworks}
            onChange={(event) =>
              setSettings((prev) => ({
                ...prev,
                complianceFrameworks: event.target.value,
              }))
            }
            placeholder="List any required frameworks or audit standards."
            rows={3}
            disabled={isSaving}
          />
        </div>

        <div>
          <button
            className="rounded-lg bg-[#DC2626] px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
            onClick={handleSave}
            disabled={isSaving}
          >
            Save
          </button>
        </div>
      </div>
    </Card>
  );
}
